import axios from 'axios'

// Use proxy configuration from vite.config.ts
const API_BASE_URL = '/api'

const configApi = axios.create({
  baseURL: `${API_BASE_URL}/config`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
configApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
configApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Country Service
export interface Country {
  id: string
  code: string
  name: string
  native_name?: string
  flag_emoji?: string
  default_currency_id: string
  default_language: string
  timezone: string
  enabled: boolean
  tax_calculation: 'inclusive' | 'exclusive'
  phone_code?: string
  continent?: string
  created_at: string
  updated_at: string
}

export interface CreateCountryDto {
  code: string
  name: string
  native_name?: string
  flag_emoji?: string
  default_currency_id: string
  default_language: string
  timezone: string
  enabled?: boolean
  tax_calculation?: 'inclusive' | 'exclusive'
  phone_code?: string
  continent?: string
}

export interface UpdateCountryDto {
  code?: string
  name?: string
  native_name?: string
  flag_emoji?: string
  default_currency_id?: string
  default_language?: string
  timezone?: string
  enabled?: boolean
  tax_calculation?: 'inclusive' | 'exclusive'
  phone_code?: string
  continent?: string
}

// Helper function to convert snake_case to camelCase
function snakeToCamel(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(v => snakeToCamel(v))
  } else if (obj !== null && obj !== undefined && typeof obj === 'object') {
    return Object.keys(obj).reduce((result, key) => {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
      result[camelKey] = snakeToCamel(obj[key])
      return result
    }, {} as any)
  }
  return obj
}

// Currency Service
export interface Currency {
  id: string
  code: string
  symbol: string
  name: string
  decimal_digits: number
  rounding: number
  symbol_position: 'before' | 'after'
  decimal_separator: string
  thousand_separator: string
  enabled: boolean
  created_at: string
  updated_at: string
}

export interface CreateCurrencyDto {
  code: string
  symbol: string
  name: string
  decimal_digits?: number
  rounding?: number
  symbol_position?: 'before' | 'after'
  decimal_separator?: string
  thousand_separator?: string
  enabled?: boolean
}

export interface UpdateCurrencyDto {
  code?: string
  symbol?: string
  name?: string
  decimal_digits?: number
  rounding?: number
  symbol_position?: 'before' | 'after'
  decimal_separator?: string
  thousand_separator?: string
  enabled?: boolean
}

// Region Service
export interface Region {
  id: string
  country_id: string
  code: string
  name: string
  type_id: string
  type_code?: string
  type_name?: string
  tax_rate: number
  enabled: boolean
  created_at: string
  updated_at: string
}

export interface CreateRegionDto {
  country_id: string
  code: string
  name: string
  type_id: string
  tax_rate?: number
  enabled?: boolean
}

export interface UpdateRegionDto {
  code?: string
  name?: string
  type_id?: string
  tax_rate?: number
  enabled?: boolean
}

// Region Type Service
export interface RegionType {
  id: string
  code: string
  name: string
  description?: string
  enabled: boolean
  created_at: string
  updated_at: string
}

// Tax Rate Service
export interface TaxRate {
  id: string
  country_id: string
  region_id?: string
  name: string
  rate: number
  type: 'vat' | 'gst' | 'pst' | 'hst' | 'sales_tax' | 'other'
  enabled: boolean
  effective_from: string
  effective_to?: string
  created_at: string
  updated_at: string
}

export interface CreateTaxRateDto {
  country_id: string
  region_id?: string
  name: string
  rate: number
  type?: 'vat' | 'gst' | 'pst' | 'hst' | 'sales_tax' | 'other'
  enabled?: boolean
  effective_from: string
  effective_to?: string
}

export interface UpdateTaxRateDto {
  name?: string
  rate?: number
  type?: 'vat' | 'gst' | 'pst' | 'hst' | 'sales_tax' | 'other'
  enabled?: boolean
  effective_from?: string
  effective_to?: string
}

// Paginated Response
export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  limit: number
  totalPages: number
}

// Filters
export interface CountryFilters {
  page?: number
  limit?: number
  enabled?: boolean
  continent?: string
  search?: string
}

export interface CurrencyFilters {
  page?: number
  limit?: number
  enabled?: boolean
  search?: string
}

export interface RegionFilters {
  page?: number
  limit?: number
  country_id?: string
  enabled?: boolean
  search?: string
  type_id?: string
}

export interface TaxRateFilters {
  page?: number
  limit?: number
  country_id?: string
  region_id?: string
  enabled?: boolean
  effective_date?: string
  search?: string
}

export const configService = {
  // Country endpoints
  countries: {
    async getAll(filters: CountryFilters = {}): Promise<PaginatedResponse<Country>> {
      const params = new URLSearchParams()
      
      if (filters.page) params.append('page', filters.page.toString())
      if (filters.limit) params.append('limit', filters.limit.toString())
      if (filters.enabled !== undefined) params.append('enabled', filters.enabled.toString())
      if (filters.continent) params.append('continent', filters.continent)
      if (filters.search) params.append('search', filters.search)

      const response = await configApi.get('/countries', { params })
      // Le backend retourne { success: true, data: [...], total: ... }
      const backendData = response.data
      return {
        data: backendData.data || [],
        total: backendData.total || 0,
        page: filters.page || 1,
        limit: filters.limit || 10,
        totalPages: Math.ceil((backendData.total || 0) / (filters.limit || 10))
      }
    },

    async getById(id: string): Promise<Country> {
      const response = await configApi.get(`/countries/${id}`)
      // Le backend retourne { success: true, data: {...} }
      const backendData = response.data
      if (!backendData.success) {
        throw new Error(backendData.error || 'Failed to fetch country')
      }
      return backendData.data
    },

    async create(countryData: CreateCountryDto): Promise<Country> {
      // Convertir les noms de champs de snake_case à camelCase pour le backend
      const dataForBackend = snakeToCamel(countryData)
      const response = await configApi.post('/countries', dataForBackend)
      // Le backend retourne { success: true, data: {...} }
      const backendData = response.data
      if (!backendData.success) {
        throw new Error(backendData.error || 'Failed to create country')
      }
      return backendData.data
    },

    async update(id: string, countryData: UpdateCountryDto): Promise<Country> {
      // Convertir les noms de champs de snake_case à camelCase pour le backend
      const dataForBackend = snakeToCamel(countryData)
      const response = await configApi.put(`/countries/${id}`, dataForBackend)
      // Le backend retourne { success: true, data: {...} }
      const backendData = response.data
      if (!backendData.success) {
        throw new Error(backendData.error || 'Failed to update country')
      }
      return backendData.data
    },

    async delete(id: string): Promise<{ message: string }> {
      const response = await configApi.delete(`/countries/${id}`)
      // Le backend retourne { success: true, message: '...' }
      const backendData = response.data
      if (!backendData.success) {
        throw new Error(backendData.error || 'Failed to delete country')
      }
      return { message: backendData.message || 'Country deleted successfully' }
    },

    async getEnabled(): Promise<Country[]> {
      const response = await configApi.get('/countries', { params: { enabled: true } })
      const backendData = response.data
      return backendData.data || []
    },

    async getByContinent(continent: string): Promise<Country[]> {
      const response = await configApi.get(`/countries/continent/${continent}`)
      return response.data.data || []
    }
  },

  // Currency endpoints
  currencies: {
    async getAll(filters: CurrencyFilters = {}): Promise<PaginatedResponse<Currency>> {
      const params = new URLSearchParams()
      
      if (filters.page) params.append('page', filters.page.toString())
      if (filters.limit) params.append('limit', filters.limit.toString())
      if (filters.enabled !== undefined) params.append('enabled', filters.enabled.toString())
      if (filters.search) params.append('search', filters.search)

      const response = await configApi.get('/currencies', { params })
      // Le backend retourne { success: true, data: [...], total: ... }
      const backendData = response.data
      return {
        data: backendData.data || [],
        total: backendData.total || 0,
        page: filters.page || 1,
        limit: filters.limit || 10,
        totalPages: Math.ceil((backendData.total || 0) / (filters.limit || 10))
      }
    },

    async getById(id: string): Promise<Currency> {
      const response = await configApi.get(`/currencies/${id}`)
      return response.data.data
    },

    async getByCode(code: string): Promise<Currency> {
      const response = await configApi.get(`/currencies/code/${code}`)
      return response.data.data
    },

    async create(currencyData: CreateCurrencyDto): Promise<Currency> {
      const response = await configApi.post('/currencies', currencyData)
      return response.data.data
    },

    async update(id: string, currencyData: UpdateCurrencyDto): Promise<Currency> {
      const response = await configApi.put(`/currencies/${id}`, currencyData)
      return response.data.data
    },

    async delete(id: string): Promise<{ message: string }> {
      const response = await configApi.delete(`/currencies/${id}`)
      return response.data
    },

    async getEnabled(): Promise<Currency[]> {
      const response = await configApi.get('/currencies', { params: { enabled: true } })
      const backendData = response.data
      return backendData.data || []
    }
  },

  // Region endpoints
  regions: {
    async getAll(filters: RegionFilters = {}): Promise<PaginatedResponse<Region>> {
      const params = new URLSearchParams()
      
      if (filters.page) params.append('page', filters.page.toString())
      if (filters.limit) params.append('limit', filters.limit.toString())
      if (filters.country_id) params.append('country_id', filters.country_id)
      if (filters.enabled !== undefined) params.append('enabled', filters.enabled.toString())
      if (filters.search) params.append('search', filters.search)
      if (filters.type_id) params.append('type_id', filters.type_id)

      const response = await configApi.get('/regions', { params })
      return response.data
    },

    async getById(id: string): Promise<Region> {
      const response = await configApi.get(`/regions/${id}`)
      return response.data.data
    },

    async getByCountry(countryId: string): Promise<Region[]> {
      const response = await configApi.get(`/regions/country/${countryId}`)
      return response.data.data || []
    },

    async create(regionData: CreateRegionDto): Promise<Region> {
      const response = await configApi.post('/regions', regionData)
      return response.data.data
    },

    async update(id: string, regionData: UpdateRegionDto): Promise<Region> {
      const response = await configApi.put(`/regions/${id}`, regionData)
      return response.data.data
    },

    async delete(id: string): Promise<{ message: string }> {
      const response = await configApi.delete(`/regions/${id}`)
      return response.data
    }
  },

  // Region Type endpoints
  regionTypes: {
    async getAll(): Promise<RegionType[]> {
      const response = await configApi.get('/region-types')
      return response.data.data || []
    },

    async getById(id: string): Promise<RegionType> {
      const response = await configApi.get(`/region-types/${id}`)
      return response.data.data || response.data
    },

    async getEnabled(): Promise<RegionType[]> {
      const response = await configApi.get('/region-types/enabled')
      return response.data.data || []
    }
  },

  // Tax Rate endpoints
  taxRates: {
    async getAll(filters: TaxRateFilters = {}): Promise<PaginatedResponse<TaxRate>> {
      const params = new URLSearchParams()
      
      if (filters.page) params.append('page', filters.page.toString())
      if (filters.limit) params.append('limit', filters.limit.toString())
      if (filters.country_id) params.append('country_id', filters.country_id)
      if (filters.region_id) params.append('region_id', filters.region_id)
      if (filters.enabled !== undefined) params.append('enabled', filters.enabled.toString())
      if (filters.effective_date) params.append('effective_date', filters.effective_date)
      if (filters.search) params.append('search', filters.search)

      const response = await configApi.get('/taxes', { params })
      const backendData = response.data
      return {
        data: backendData.data || [],
        total: backendData.total || 0,
        page: filters.page || 1,
        limit: filters.limit || 10,
        totalPages: Math.ceil((backendData.total || 0) / (filters.limit || 10))
      }
    },

    async getById(id: string): Promise<TaxRate> {
      const response = await configApi.get(`/taxes/${id}`)
      const backendData = response.data
      if (!backendData.success) {
        throw new Error(backendData.error || 'Failed to fetch tax rate')
      }
      return backendData.data
    },

    async getByCountry(countryId: string): Promise<TaxRate[]> {
      const response = await configApi.get(`/taxes/country/${countryId}`)
      const backendData = response.data
      return backendData.data || []
    },

    async getByRegion(regionId: string): Promise<TaxRate[]> {
      const response = await configApi.get(`/taxes/region/${regionId}`)
      const backendData = response.data
      return backendData.data || []
    },

    async getCurrentByCountry(countryId: string): Promise<TaxRate[]> {
      const response = await configApi.get(`/taxes/country/${countryId}/current`)
      const backendData = response.data
      return backendData.data || []
    },

    async create(taxRateData: CreateTaxRateDto): Promise<TaxRate> {
      const response = await configApi.post('/taxes', taxRateData)
      const backendData = response.data
      if (!backendData.success) {
        throw new Error(backendData.error || 'Failed to create tax rate')
      }
      return backendData.data
    },

    async update(id: string, taxRateData: UpdateTaxRateDto): Promise<TaxRate> {
      const response = await configApi.put(`/taxes/${id}`, taxRateData)
      const backendData = response.data
      if (!backendData.success) {
        throw new Error(backendData.error || 'Failed to update tax rate')
      }
      return backendData.data
    },

    async delete(id: string): Promise<{ message: string }> {
      const response = await configApi.delete(`/taxes/${id}`)
      const backendData = response.data
      if (!backendData.success) {
        throw new Error(backendData.error || 'Failed to delete tax rate')
      }
      return { message: backendData.message || 'Tax rate deleted successfully' }
    }
  },

  // Configuration endpoints
  config: {
    async getSystemConfig(): Promise<{
      default_currency: string
      default_country: string
      default_language: string
      timezone: string
      tax_calculation: 'inclusive' | 'exclusive'
    }> {
      const response = await configApi.get('/system-config')
      return response.data
    },

    async updateSystemConfig(configData: {
      default_currency?: string
      default_country?: string
      default_language?: string
      timezone?: string
      tax_calculation?: 'inclusive' | 'exclusive'
    }): Promise<{ message: string }> {
      const response = await configApi.put('/system-config', configData)
      return response.data
    },

    async getStatistics(): Promise<{
      countries: { total: number; enabled: number }
      currencies: { total: number; enabled: number }
      regions: { total: number; by_country: Record<string, number> }
      tax_rates: { total: number; active: number }
    }> {
      const response = await configApi.get('/statistics')
      return response.data
    }
  }
}

export default configService
